import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Clock, AlertTriangle, CalendarPlus, TrendingDown, Ban, Building2, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SuperAdminSubscriptions() {
  const [expiring, setExpiring] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line no-unused-vars
  const token = localStorage.getItem('superAdminToken');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  const fetchData = useCallback(async () => {
    try {
      const [expRes, tenRes] = await Promise.all([
        axios.get(`${API}/platform/subscriptions/expiring`, { headers: getHeaders() }),
        axios.get(`${API}/platform/tenants`, { headers: getHeaders() }),
      ]);
      setExpiring(expRes.data);
      setTenants(tenRes.data);
    } catch { toast.error('Failed to load subscription data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const extendTrial = async (tenantId, days) => {
    try {
      await axios.post(`${API}/platform/subscriptions/${tenantId}/extend-trial?days=${days}`, {}, { headers: getHeaders() });
      toast.success(`Trial extended by ${days} days`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to extend trial'); }
  };

  const downgradeTenant = async (tenantId, plan) => {
    if (!window.confirm(`Downgrade this tenant to ${plan}?`)) return;
    try {
      await axios.post(`${API}/platform/subscriptions/${tenantId}/downgrade?target_plan=${plan}`, {}, { headers: getHeaders() });
      toast.success(`Tenant downgraded to ${plan}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to downgrade'); }
  };

  const suspendOverdue = async () => {
    if (!window.confirm('Suspend all tenants with overdue payments (>30 days)? This will disable their access.')) return;
    try {
      const res = await axios.post(`${API}/platform/subscriptions/suspend-overdue`, {}, { headers: getHeaders() });
      toast.success(`${res.data.suspended_count} tenants suspended`);
      fetchData();
    } catch { toast.error('Failed to process overdue suspensions'); }
  };

  const planOrder = { enterprise: 4, pro: 3, starter: 2, free: 1 };
  const paidTenants = tenants.filter(t => t.plan && t.plan !== 'free' && t.status === 'active');
  const freeTenants = tenants.filter(t => (!t.plan || t.plan === 'free') && t.status === 'active');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" /></div>;

  return (
    <div className="space-y-6" data-testid="subscriptions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Subscription Management</h1>
          <p className="text-slate-400 mt-1">Trial tracking, plan changes, and overdue handling</p>
        </div>
        <Button variant="outline" className="border-red-700/50 text-red-400 hover:bg-red-900/30" onClick={suspendOverdue} data-testid="suspend-overdue-btn">
          <Ban size={14} className="mr-2" />Suspend Overdue
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-5 text-center">
            <p className="text-2xl font-extrabold text-white">{paidTenants.length}</p>
            <p className="text-xs text-slate-400 uppercase tracking-wider mt-1">Paid Tenants</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-5 text-center">
            <p className="text-2xl font-extrabold text-white">{freeTenants.length}</p>
            <p className="text-xs text-slate-400 uppercase tracking-wider mt-1">Free/Trial</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-5 text-center">
            <p className="text-2xl font-extrabold text-amber-400">{expiring.filter(e => e.lifecycle_status === 'trial_expiring').length}</p>
            <p className="text-xs text-slate-400 uppercase tracking-wider mt-1">Expiring Soon</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-5 text-center">
            <p className="text-2xl font-extrabold text-red-400">{expiring.filter(e => e.lifecycle_status === 'trial_expired').length}</p>
            <p className="text-xs text-slate-400 uppercase tracking-wider mt-1">Expired</p>
          </CardContent>
        </Card>
      </div>

      {/* Expiring Trials */}
      {expiring.length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2"><AlertTriangle size={18} className="text-amber-400" />Trials Expiring / Expired</CardTitle>
            <CardDescription className="text-slate-400">Free plan tenants whose trial window is ending or has ended</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400 font-bold">Tenant</TableHead>
                  <TableHead className="text-slate-400 font-bold">Status</TableHead>
                  <TableHead className="text-slate-400 font-bold">Days Left</TableHead>
                  <TableHead className="text-slate-400 font-bold">Trial Ends</TableHead>
                  <TableHead className="text-slate-400 font-bold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expiring.map(t => (
                  <TableRow key={t.id} className="border-slate-700 hover:bg-slate-800/50" data-testid={`expiring-${t.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 size={14} className="text-slate-500" />
                        <div>
                          <p className="text-sm font-semibold text-white">{t.name}</p>
                          <p className="text-xs text-slate-500">{t.slug}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={t.lifecycle_status === 'trial_expired' ? 'bg-red-500/20 text-red-400 border-red-500/30 border' : 'bg-amber-500/20 text-amber-400 border-amber-500/30 border'}>
                        {t.lifecycle_status === 'trial_expired' ? 'Expired' : 'Expiring'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-white font-bold">{t.days_remaining}</TableCell>
                    <TableCell className="text-slate-400 text-xs">{t.trial_end?.slice(0, 10)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="text-emerald-400 hover:text-emerald-300 h-7 text-xs" onClick={() => extendTrial(t.id, 14)}>
                          <CalendarPlus size={12} className="mr-1" />+14 days
                        </Button>
                        <Button size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300 h-7 text-xs" onClick={() => extendTrial(t.id, 30)}>
                          +30 days
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* All Paid Subscriptions */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-white">Active Paid Subscriptions</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700">
                <TableHead className="text-slate-400 font-bold">Tenant</TableHead>
                <TableHead className="text-slate-400 font-bold">Plan</TableHead>
                <TableHead className="text-slate-400 font-bold">Currency</TableHead>
                <TableHead className="text-slate-400 font-bold">Users</TableHead>
                <TableHead className="text-slate-400 font-bold">Created</TableHead>
                <TableHead className="text-slate-400 font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paidTenants.length > 0 ? paidTenants.sort((a, b) => (planOrder[b.plan] || 0) - (planOrder[a.plan] || 0)).map(t => (
                <TableRow key={t.id} className="border-slate-700 hover:bg-slate-800/50" data-testid={`sub-${t.id}`}>
                  <TableCell>
                    <div>
                      <p className="text-sm font-semibold text-white">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.slug}</p>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="border-violet-500/50 text-violet-400">{t.plan}</Badge></TableCell>
                  <TableCell className="text-slate-300 text-sm">{t.currency || 'USD'}</TableCell>
                  <TableCell className="text-white">{t.user_count || 0}</TableCell>
                  <TableCell className="text-slate-400 text-xs">{t.created_at?.slice(0, 10)}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" className="text-amber-400 hover:text-amber-300 h-7 text-xs" onClick={() => downgradeTenant(t.id, 'free')} data-testid={`downgrade-${t.id}`}>
                      <TrendingDown size={12} className="mr-1" />Downgrade
                    </Button>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No paid subscriptions yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
